import { CopyOutlined, UserOutlined } from "@ant-design/icons";
import {
  Avatar,
  Button,
  Card,
  DatePicker,
  Divider,
  Flex,
  Form,
  Image,
  Input,
  message,
  Select,
  Typography,
} from "antd";
import { useForm } from "antd/es/form/Form";
import CardSectionTitle from "components/CardSectionTitle.component";
import { showConfirmModal } from "components/modals/confirmModal";
import { DeactivateClientModal } from "components/modals/DeactivateClientModal.component";
import { DeactivateStaffModal } from "components/modals/DeactivateStaffModal.component";
import { useAuth } from "context/AuthContextProvider";
import { StatusBadge } from "pages/schedule/component/ScheduleStatusBadge";
import {
  useDeactivateUser,
  useReactivateUser,
  useSetStaffCalendarColor,
  useUpdateUser,
} from "hooks/mutations/user";
import { useEffect, useState } from "react";
import { translate } from "shared/languages";
import { User } from "shared/types";
import { Gender, NAME_SPLIT_TYPES, UserType, karyawanTypeOf, phoneOptionalForRoles } from "shared/types/enum";
import { ACCOUNT_STATUS_LABELS, ACCOUNT_STATUS_TONE, isUserDeactivated, USER_ROLE_LABELS } from "shared/utils";
import { hasRole } from "shared/utils/role";
import { useAdminCapabilities } from "hooks/useAdminCapabilities";
import { AdminCapability } from "shared/utils/capabilities";
import PhoneInput, { phoneRules } from "shared/components/PhoneInput";
import { CalendarColorPicker } from "./CalendarColorPicker";
import {
  DEFAULT_DATE_FORMAT,
  DEFAULT_DATE_SUBMIT_FORMAT,
  formatDate,
  now,
  tzDate,
} from "shared/utils/dayjs";

const { Option } = Select;

// Copy with the modern async API, falling back to the hidden-textarea
// execCommand trick (http origins / older WebViews). Returns whether the text
// actually reached the clipboard so the caller can toast accordingly.
const copyTextToClipboard = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
};

// The highest-privilege role an account genuinely holds, by a fixed
// precedence (Manager is additive — a Manager's roles always include
// "admin" too — so without a precedence a dual-role account is ambiguous).
// SysAdmin checked first: it's the highest-privilege role everywhere else
// it's checked (server-side auth always evaluates SystemAdmin before any
// business-scoped role), so an account that somehow held both should still
// display as SysAdmin, not be understated as Manager. Exported so a
// self-profile page (which always knows its own account, no route-sharing
// ambiguity — contrast getDisplayUserType below) can pass this in as the
// correct thisUserType up front instead of guessing.
export const getPrimaryUserType = (roles: string[]): UserType | undefined => {
  if (roles.includes(UserType.SysAdmin)) return UserType.SysAdmin;
  if (roles.includes(UserType.Manager)) return UserType.Manager;
  if (roles.includes(UserType.Admin)) return UserType.Admin;
  if (roles.includes(UserType.Instructor)) return UserType.Instructor;
  // Between Instructor and Customer: a non-teaching Staf who also books as a
  // client reads "Staf" on the Karyawan page (thisUserType Instructor is not
  // held, so this fallback decides) and "Klien" on the Klien page.
  if (roles.includes(UserType.Staff)) return UserType.Staff;
  if (roles.includes(UserType.Customer)) return UserType.Customer;
  return undefined;
};

// Admin/Manager rows are opened via the same staff/instructor detail route
// (thisUserType is hardcoded to Instructor there — see InstructorDetails.tsx),
// so the "Peran" label can't just trust that prop or it always reads
// "instruktur" regardless of the viewed user's real role.
//
// Trust thisUserType FIRST when the account genuinely holds that role — this
// is what makes a dual-role person (e.g. instructor AND customer on the same
// account) display correctly on BOTH of their profile pages: "Klien" on the
// customer page, "Instruktur" on the instructor page, matching whichever one
// you're actually looking at, rather than a fixed priority always winning
// regardless of context. Only fall back to the priority-derived guess when
// thisUserType ISN'T one of the account's real roles at all — i.e. the
// actual InstructorDetails.tsx quirk (an Admin/Manager target, whose roles
// never include "instructor").
const getDisplayUserType = (user: User, fallback: UserType): UserType => {
  if (user.roles.includes(fallback)) return fallback;
  return getPrimaryUserType(user.roles) ?? fallback;
};

interface UserProfileCardProps {
  user: User;
  thisUserType: UserType;
  /**
   * Login code for the customer/Spott or instructor app (Kode Masuk Klien /
   * Kode Masuk Instruktur) — shown read-only, for Customer and Instructor
   * profiles only.
   */
  loginCode?: string | null;
  /**
   * Magic login link for the same app (Tautan Masuk) — shown with a copy
   * affordance plus a regenerate action, for Customer and Instructor profiles
   * only. Absent against an older server, which hides the row.
   */
  loginLink?: string | null;
  onRegenerateLoginLink?: () => void;
  isRegeneratingLoginLink?: boolean;
  /**
   * Replace the login code above with a fresh one. Codes are stored per surface
   * (client vs instructor), so rotating one never disturbs the same person's
   * other code — which is what makes this safe to offer.
   */
  onRegenerateLoginCode?: () => void;
  isRegeneratingLoginCode?: boolean;
}

const UserProfileCard = ({
  user,
  thisUserType,
  loginCode,
  loginLink,
  onRegenerateLoginLink,
  isRegeneratingLoginLink,
  onRegenerateLoginCode,
  isRegeneratingLoginCode,
}: UserProfileCardProps) => {
  const isAdmin = user.roles.includes(UserType.Admin);
  const isTargetManager = user.roles.includes(UserType.Manager);
  const isDeactivated = isUserDeactivated(user, thisUserType);
  const [form] = useForm();
  const { state } = useAuth();
  const isCurrentUser = state.user?.id === user.id;
  const businessId = state.business?.id ?? "";
  const isSysAdminActor = hasRole(state.user?.roles, UserType.SysAdmin);
  const isManagerActor = hasRole(state.user?.roles, UserType.Manager);
  // Pembatasan Admin. Two separate things:
  //  - regenerating kills the person's current credential and signs them out,
  //    so it can be Manager-only on its own;
  //  - the whole credential row can be taken away, per surface, because a code
  //    or a link IS a working sign-in to that account.
  // The view restriction supersedes the regenerate one: a row that is not shown
  // has no buttons to disable. (Safe under SystemAdminLayout too: the hook
  // skips the fetch for them.)
  const { isRestricted } = useAdminCapabilities();
  const linkRestricted = isRestricted(AdminCapability.LoginLinkRegenerate);
  const showsCredentials =
    thisUserType === UserType.Customer || thisUserType === UserType.Instructor;
  const credentialsRestricted =
    showsCredentials &&
    isRestricted(
      thisUserType === UserType.Customer
        ? AdminCapability.LoginCredentialsClient
        : AdminCapability.LoginCredentialsInstructor,
    );
  // Mirrors the server's setUserDeletedAt check; must stay in sync with it.
  // SysAdmin may deactivate any admin-tier account. A Manager may deactivate
  // only a plain Admin, never a peer Manager (`isCurrentUser` above already
  // covers the self-action case client-side; the server also scopes a
  // Manager to their own business).
  const canDeactivateAdmin =
    isSysAdminActor || (isManagerActor && !isTargetManager);
  // Reactivating an admin account is SystemAdmin-only, even for a Manager who
  // could deactivate it — reactivation is functionally "granting a seat",
  // same category as creating one, so it stays fully Spott-gated.
  const canReactivateAdmin = isSysAdminActor;
  // Deactivated users can be reactivated regardless of role (admins included;
  // the admin gate is enforced server-side).
  const isReactivable = isDeactivated;
  // Which name UI to show is decided by the account's REAL role, not the
  // raw thisUserType prop — InstructorDetails.tsx hardcodes thisUserType to
  // Instructor even when viewing an Admin/Manager account through that same
  // route (see getDisplayUserType above), so trusting it directly would
  // incorrectly show the split-name fields for an admin. Nama Belakang is
  // always optional and clearable (a blank save stores NULL) — mirrors the
  // backend rule in updateUserById exactly.
  const resolvedUserType = getDisplayUserType(user, thisUserType);
  const usesNameSplit = NAME_SPLIT_TYPES.includes(resolvedUserType);
  // The Peran row names the role this page is about; a person holding both
  // an admin tier and the instructor role gets the other one beside it.
  const secondaryStaffRole: UserType | null =
    resolvedUserType === UserType.Instructor && isAdmin
      ? isTargetManager
        ? UserType.Manager
        : UserType.Admin
      : (resolvedUserType === UserType.Admin || resolvedUserType === UserType.Manager) &&
        user.roles.includes(UserType.Instructor)
      ? UserType.Instructor
      : null;

  // Jabatan is a non-teaching Staf's field only (same predicate the phone
  // rule uses); other profiles neither show nor send it.
  // Jabatan belongs to a non-teaching Staf only: someone holding the staff
  // role and not the instructor role. karyawanTypeOf alone also answered Staf
  // for clients and admin accounts, which showed them a Jabatan field the
  // server never saves (it writes job_title only for the staff role).
  const isNonTeachingStaf =
    user.roles.includes(UserType.Staff) &&
    karyawanTypeOf(user.roles) === UserType.Staff;
  // The calendar colour belongs to anyone who teaches.
  const isInstructorTarget = user.roles.includes(UserType.Instructor);
  // Another admin's profile is read-only to you, but if they also teach, their
  // calendar colour is not: it is a setting of the studio's calendar, not part
  // of their identity. Such a form saves the colour alone, through its own
  // endpoint, and sends none of the identity fields.
  const profileReadOnly = isAdmin && !isCurrentUser;
  const formDisabled = profileReadOnly || isDeactivated;

  const formValues = {
    job_title: user.staff?.job_title ?? undefined,
    calendar_color: user.staff?.calendar_color ?? undefined,
    full_name: user.full_name,
    first_name: user.first_name ?? undefined,
    middle_name: user.middle_name ?? undefined,
    last_name: user.last_name ?? undefined,
    email: user.email ?? undefined,
    phone_number: user.phone_number ?? undefined,
    gender: user.gender ?? undefined,
    dob: user.dob ? tzDate(user.dob) : undefined,
    created_at: user.created_at,
  };

  const { mutate: updateUser, isSuccess } = useUpdateUser();
  const { mutate: setCalendarColor, isSuccess: isColorSaved } =
    useSetStaffCalendarColor();
  const { mutate: deactivateUser, isPending: isDeactivating } =
    useDeactivateUser();
  const { mutate: reactivateUser, isPending: isReactivating } =
    useReactivateUser();
  const [isFieldChanged, setIsFieldChanged] = useState(false);
  // Nonaktifkan opens DeactivateClientModal for a client and
  // DeactivateStaffModal for everyone else; both ask the server what the
  // deactivation would run into first.
  const [clientDeactivateOpen, setClientDeactivateOpen] = useState(false);
  const [staffDeactivateOpen, setStaffDeactivateOpen] = useState(false);

  const submitFieldChange = () => {
    setIsFieldChanged(true);
  };

  // The read-only profile's save: the colour alone, read straight off the
  // form so the identity fields it can't change are never validated or sent.
  const handleSaveColorOnly = () => {
    const calendarColor = form.getFieldValue("calendar_color");
    if (!isInstructorTarget || !calendarColor) return;
    setCalendarColor({
      userId: user.id,
      calendarColor,
      fullName: user.full_name,
    });
  };

  const handleSubmitChange = (values: any) => {
    updateUser({
      updateForm: {
        ...values,
        // Disambiguates which name-shape (full_name vs first/last) the
        // backend should expect/require. Uses the same role-derived value
        // as usesNameSplit above (not the raw thisUserType prop, which
        // InstructorDetails.tsx hardcodes even for Admin/Manager targets).
        user_type: resolvedUserType,
        business_id: businessId,
        // Only when set: tzDate(undefined) is "now", which used to stamp
        // today's date as the birth date of anyone saved without one.
        dob: values.dob ? tzDate(values.dob).format(DEFAULT_DATE_SUBMIT_FORMAT) : undefined,
        // A cleared field reaches the server as "" and means "no phone";
        // the server allows that only for a non-teaching Staf.
        phone_number: values.phone_number ?? "",
        // Jabatan travels only when the field is shown; blank clears it.
        ...(isNonTeachingStaf ? { job_title: values.job_title ?? "" } : {}),
        // Only when the admin picked one; an untouched field leaves the saved
        // colour as it is.
        ...(isInstructorTarget && values.calendar_color
          ? { calendar_color: values.calendar_color }
          : {}),
      },
      userId: user.id,
    });
  };

  // Disable Update Button when the update is successful
  useEffect(() => {
    if (isSuccess || isColorSaved) {
      setIsFieldChanged(false);
    }
  }, [isSuccess, isColorSaved]);

  // AntD Form's `initialValues` is only ever consumed on the form's first
  // mount — a later prop change (e.g. `user` refetching with fresh data
  // after a save, or after navigating back to this same customer/instructor
  // without a full page reload) does NOT re-apply it on its own. Without
  // this, the visible fields can keep showing what they were on mount even
  // though the underlying data has already moved on. React Query returns a
  // stable reference when the data hasn't structurally changed, so this
  // only actually fires when `user` genuinely differs, not on every render.
  useEffect(() => {
    form.setFieldsValue(formValues);
    setIsFieldChanged(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const doDeactivate = (
    options: { removeUpcoming?: boolean; onSuccess?: () => void; onError?: () => void } = {}
  ) =>
    deactivateUser(
      {
        userId: user.id,
        // TODO: Staff should be removed from the user type -- instructor and admin shud be separated
        params: {
          user_type: thisUserType,
          ...(options.removeUpcoming ? { remove_upcoming_classes: true } : {}),
        },
      },
      { onSuccess: options.onSuccess, onError: options.onError }
    );

  // A client gets DeactivateClientModal: their deactivation is refused while
  // a booking is in progress, and locks their packages, bookings and rewards
  // at this studio. Everyone else gets DeactivateStaffModal: refused while
  // they still teach a class that hasn't started, and an admin-tier target's
  // confirm carries the stricter copy (a Manager can't undo it, only
  // SystemAdmin can reactivate an admin account, see setUserDeletedAt).
  const handleDeactivateClick = () => {
    if (thisUserType === UserType.Customer) {
      setClientDeactivateOpen(true);
      return;
    }
    setStaffDeactivateOpen(true);
  };

  return (
    <Card>
      {thisUserType === UserType.Customer && (
        <DeactivateClientModal
          open={clientDeactivateOpen}
          userId={user.id}
          deactivating={isDeactivating}
          onConfirm={() =>
            doDeactivate({ onSuccess: () => setClientDeactivateOpen(false) })
          }
          onClose={() => setClientDeactivateOpen(false)}
        />
      )}
      {thisUserType !== UserType.Customer && (
        <DeactivateStaffModal
          open={staffDeactivateOpen}
          user={user}
          isSysAdminActor={isSysAdminActor}
          pending={isDeactivating}
          onConfirm={(removeUpcoming, onRefused) =>
            doDeactivate({
              removeUpcoming,
              onSuccess: () => setStaffDeactivateOpen(false),
              // Refused because a class appeared meanwhile: show it.
              onError: onRefused,
            })
          }
          onClose={() => setStaffDeactivateOpen(false)}
        />
      )}
      <Flex vertical gap={20}>
        <CardSectionTitle
          title={translate("title.personal_information")}
          subtitle={
            isCurrentUser
              ? translate("subtitle.profile")
              : thisUserType === UserType.Customer
              ? translate("subtitle.customer_profile")
              : translate("subtitle.staff_profile")
          }
        />
        <Form
          form={form}
          name="user-profile"
          labelAlign="left"
          labelCol={{ span: 6 }}
          colon={false}
          requiredMark={false}
          onFinish={handleSubmitChange}
          initialValues={formValues}
          onFieldsChange={submitFieldChange}
          // Disables if its admin or not current user
          disabled={formDisabled}
        >
          <Flex>
            {user.photo_url ? (
              <Avatar src={<Image src={user.photo_url} alt="avatar" />} />
            ) : (
              <Avatar size={50} icon={<UserOutlined />} />
            )}
          </Flex>
          <Divider />
          {usesNameSplit ? (
            <>
              <Form.Item
                name="first_name"
                label={translate("label.first_name")}
                rules={[
                  {
                    required: true,
                    message: `${translate("form.error.input")} ${translate(
                      "label.first_name"
                    )} ${translate("form.error.input_end")}`,
                  },
                ]}
              >
                <Input
                  placeholder={`${translate(
                    "form.placeholders.enter"
                  )} ${translate("label.first_name")}`}
                />
              </Form.Item>
              <Form.Item name="middle_name" label={translate("label.middle_name")}>
                <Input
                  placeholder={`${translate(
                    "form.placeholders.enter"
                  )} ${translate("label.middle_name")}`}
                />
              </Form.Item>
              <Form.Item name="last_name" label={translate("label.last_name")}>
                <Input
                  placeholder={`${translate(
                    "form.placeholders.enter"
                  )} ${translate("label.last_name")}`}
                />
              </Form.Item>
            </>
          ) : (
            <Form.Item
              name="full_name"
              label={translate("label.name")}
              rules={[
                {
                  required: true,
                  message: `${translate("form.error.input")} ${translate(
                    "label.name"
                  )} ${translate("form.error.input_end")}`,
                },
              ]}
            >
              <Input
                placeholder={`${translate("form.placeholders.enter")} ${translate(
                  "label.full_name"
                )}`}
              />
            </Form.Item>
          )}
          <Form.Item name="created_at" label={translate("label.join_date")}>
            <Typography>{formatDate(user.created_at)}</Typography>
          </Form.Item>

          <Form.Item name="user_type" label={translate("label.role")}>
            <Flex gap={6} wrap>
              <StatusBadge
                tone="navy"
                label={USER_ROLE_LABELS[getDisplayUserType(user, thisUserType)]}
                dot={false}
              />
              {/* Multi-role staff: the other hat, when the account holds both
                  an admin tier and the instructor role. Every role chip is
                  navy, whichever position it sits in. */}
              {secondaryStaffRole && (
                <StatusBadge
                  tone="navy"
                  label={USER_ROLE_LABELS[secondaryStaffRole]}
                  dot={false}
                />
              )}
              {isDeactivated && (
                <StatusBadge tone={ACCOUNT_STATUS_TONE.inactive} label={ACCOUNT_STATUS_LABELS.inactive} />
              )}
            </Flex>
          </Form.Item>

          {/* Not for a system admin: the picker reads the studio's instructor
              list, a studio-scoped endpoint that answers their token with a
              401, which signs them out. */}
          {isInstructorTarget && !isSysAdminActor && (
            <Form.Item
              name="calendar_color"
              label={translate("label.calendar_color")}
              tooltip={translate("label.calendar_color_hint")}
            >
              {/* Its own disabled prop, not the form's: open on another
                  admin's read-only profile too (see profileReadOnly). */}
              <CalendarColorPicker instructorId={user.id} disabled={isDeactivated} />
            </Form.Item>
          )}

          {isNonTeachingStaf && (
            <Form.Item name="job_title" label={translate("label.job_title")}>
              <Input
                maxLength={60}
                placeholder={`${translate("form.placeholders.enter")} ${translate("label.job_title")}`}
              />
            </Form.Item>
          )}

          <Divider />
          {/* Read-only: the customer/instructor's login code for the booking /
              instructor app. Not part of the editable form values — display +
              copy only. Omitted ENTIRELY when a Manager has restricted this
              surface's credentials: the code is rendered copyable, so
              disabling a button would leave it readable on screen. */}
          {showsCredentials &&
            !credentialsRestricted &&
            loginCode && (
            <Form.Item
              label={translate(
                thisUserType === UserType.Customer
                  ? "label.login_code"
                  : "label.instructor_login_code",
              )}
            >
              <Flex align="center" gap={8} wrap>
                <Typography.Text code copyable style={{ letterSpacing: 1 }}>
                  {loginCode}
                </Typography.Text>
                {onRegenerateLoginCode && (
                  <Button
                    type="primary"
                    size="small"
                    // Explicit prop for the same reason as the link button
                    // below: the surrounding <Form disabled> propagates through
                    // AntD's DisabledContext and would kill this in its main use
                    // case (an admin viewing someone else's profile).
                    disabled={
                      isDeactivated || isRegeneratingLoginCode || linkRestricted
                    }
                    onClick={() =>
                      showConfirmModal({
                        tone: "amber",
                        title: translate(
                          "messages.warning.regenerate_login_code.title"
                        ),
                        content: translate(
                          "messages.warning.regenerate_login_code.content"
                        ),
                        confirmLabel: translate(
                          "button.regenerate_login_code"
                        ),
                        top: "31vh",
                        onConfirm: onRegenerateLoginCode,
                      })
                    }
                  >
                    {translate("button.regenerate_login_code")}
                  </Button>
                )}
              </Flex>
            </Form.Item>
          )}

          {/* Magic login link: the URL itself is never shown, the copy button
              puts it on the clipboard. Regenerating kills the old link
              immediately and ends the sessions opened with it. Omitted with
              the code row above when this surface is restricted. */}
          {showsCredentials &&
            !credentialsRestricted &&
            loginLink && (
            <Form.Item
              label={translate(
                thisUserType === UserType.Customer
                  ? "label.login_link"
                  : "label.instructor_login_link",
              )}
            >
              <Flex align="center" gap={8} wrap>
                {/* The URL itself stays hidden (it is a standalone login
                    credential — no reason to have it readable on screen);
                    the button puts the full link on the clipboard. Explicit
                    disabled overrides the surrounding <Form disabled>
                    (DisabledContext), same as the regenerate button below. */}
                <Button
                  size="small"
                  icon={<CopyOutlined />}
                  disabled={false}
                  onClick={() => {
                    void copyTextToClipboard(loginLink).then((ok) =>
                      ok
                        ? message.success(
                            translate("messages.success.copy_login_link")
                          )
                        : message.error(translate("messages.error.general"))
                    );
                  }}
                >
                  {translate("button.copy_login_link")}
                </Button>
                {onRegenerateLoginLink && (
                  <Button
                    type="primary"
                    size="small"
                    // Explicit prop on purpose: the surrounding <Form disabled>
                    // (admin viewing someone else's profile) propagates via
                    // AntD's DisabledContext and would disable this button in
                    // its primary use case; an explicit prop overrides the
                    // context. Deactivated users keep it disabled — their
                    // link can't log in anyway. A Manager's restriction
                    // disables it too, with no hover reason (by decision).
                    disabled={
                      isDeactivated || isRegeneratingLoginLink || linkRestricted
                    }
                    onClick={() =>
                      showConfirmModal({
                        tone: "amber",
                        title: translate(
                          "messages.warning.regenerate_login_link.title"
                        ),
                        content: translate(
                          "messages.warning.regenerate_login_link.content"
                        ),
                        confirmLabel: translate(
                          "button.regenerate_login_link"
                        ),
                        // ~30% of the free space above, ~70% below (Andrew's
                        // pick: a touch higher than centered). Value tuned by
                        // measuring the rendered dialog under the body zoom.
                        top: "31vh",
                        onConfirm: onRegenerateLoginLink,
                      })
                    }
                  >
                    {translate("button.regenerate_login_link")}
                  </Button>
                )}
              </Flex>
            </Form.Item>
          )}

          <Form.Item
            name="email"
            label="Email"
            rules={[{ type: "email", message: translate("form.error.email") }]}
          >
            <Input
              placeholder={`${translate("form.placeholders.enter")} email`}
            />
          </Form.Item>
          <Form.Item
            name="phone_number"
            label={translate("label.phone_number")}
            // Optional only for a non-teaching Staf (same predicate as the
            // server): they never log in, so there is nobody to send a code to.
            rules={phoneRules({ required: !phoneOptionalForRoles(user.roles) })}
          >
            <PhoneInput
              placeholder={`${translate("form.placeholders.enter")} ${translate(
                "form.placeholders.phone_number"
              )}`}
              disabled={isAdmin || isDeactivated}
            />
          </Form.Item>
          <Divider />
          <Form.Item name="gender" label={translate("label.gender")}>
            <Select
              placeholder={`${translate(
                "form.placeholders.select"
              )} ${translate("label.gender")}`}
            >
              {Object.entries(Gender).map(([key, value]) => {
                return (
                  <Option key={key} value={value}>
                    {translate(`form.select.gender.${value}`)}
                  </Option>
                );
              })}
            </Select>
          </Form.Item>
          <Form.Item name="dob" label={translate("label.dob")}>
            <DatePicker
              style={{ width: "100%" }}
              placeholder={`${translate("form.placeholders.enter")} ${translate(
                "label.dob"
              )}`}
              format={DEFAULT_DATE_FORMAT}
              disabledDate={(current) =>
                current && current > now().endOf("day")
              }
            />
          </Form.Item>
          <Form.Item>
            <Flex gap={10}>
              {/* Button system: navy confirm + soft-red destructive. */}
                <Button
                  type="primary"
                  htmlType={profileReadOnly ? "button" : "submit"}
                  onClick={profileReadOnly ? handleSaveColorOnly : undefined}
                  disabled={!isFieldChanged}
                >
                  {translate("button.save_changes")}
                </Button>
              {/* Don't show if user is already deactivated or is current user */}
              {/* Disabled if current user is not system admin and its an admin user */}
              {!isDeactivated && !isCurrentUser && (
                <Button
                  variant="filled"
                  color="danger"
                  disabled={!canDeactivateAdmin && isAdmin}
                  loading={isDeactivating}
                  onClick={handleDeactivateClick}
                >
                  {translate("button.deactivate_user")}
                </Button>
              )}
              {isReactivable && !isCurrentUser && (
                <Button
                  variant="filled"
                  color="danger"
                  loading={isReactivating}
                  disabled={!canReactivateAdmin && isAdmin}
                  onClick={() =>
                    reactivateUser({
                      userId: user.id,
                      params: { user_type: thisUserType },
                    })
                  }
                >
                  {translate("button.activate_user")}
                </Button>
              )}
            </Flex>
          </Form.Item>
        </Form>
      </Flex>
    </Card>
  );
};

export default UserProfileCard;
